import React, { useState } from 'react';
import {
  Box,
  SpaceBetween,
  RadioGroup,
  Checkbox,
  Select,
  Multiselect,
  Grid,
  Button,
  Alert
} from '@amzn/awsui-components-react';
import HourlyTab from './HourlyTab';
import { getWorkingDayOptions, getCalendarDayOptions } from '../Utils/GeneralUtils';

const Val = ({ children }) => <Box variant="span" fontWeight="bold">{children}</Box>;

const DailyTab = ({ isViewMode, onConfigChange, initialConfig = {} }) => {
  const [dayType, setDayType] = useState('calendar');
  const [workingDayOption, setWorkingDayOption] = useState('range');
  const [dailyWorkingDayBegin, setDailyWorkingDayBegin] = useState({ label: 'WD1', value: 'WD1' });
  const [dailyWorkingDayEnd, setDailyWorkingDayEnd] = useState({ label: 'WD23', value: 'WD23' });
  const [specificWorkingDays, setSpecificWorkingDays] = useState([]);
  const [calendarDayOption, setCalendarDayOption] = useState('range');
  const [dailyCalendarDayBegin, setDailyCalendarDayBegin] = useState({ label: '1', value: '1' });
  const [dailyCalendarDayEnd, setDailyCalendarDayEnd] = useState({ label: '31', value: '31' });
  const [specificCalendarDays, setSpecificCalendarDays] = useState([]);
  const [dailyEveryXDays, setDailyEveryXDays] = useState(false);
  const [dailyEveryXDaysValue, setDailyEveryXDaysValue] = useState('2');
  const [showHourlyConfig, setShowHourlyConfig] = useState(false);
  const [hourlyConfig, setHourlyConfig] = useState({});

  React.useEffect(() => {
    if (Object.keys(initialConfig).length > 0) {
      setDayType(initialConfig.workingDay ? 'working' : 'calendar');
      setWorkingDayOption(initialConfig.workingDayOption || 'range');
      if (initialConfig.workingDayBegin) setDailyWorkingDayBegin({ label: initialConfig.workingDayBegin, value: initialConfig.workingDayBegin });
      if (initialConfig.workingDayEnd) setDailyWorkingDayEnd({ label: initialConfig.workingDayEnd, value: initialConfig.workingDayEnd });
      if (initialConfig.specificWorkingDays) setSpecificWorkingDays(initialConfig.specificWorkingDays.map(d => ({ label: d, value: d })));
      setCalendarDayOption(initialConfig.calendarDayOption || 'range');
      if (initialConfig.calendarDayBegin) setDailyCalendarDayBegin({ label: initialConfig.calendarDayBegin, value: initialConfig.calendarDayBegin });
      if (initialConfig.calendarDayEnd) setDailyCalendarDayEnd({ label: initialConfig.calendarDayEnd, value: initialConfig.calendarDayEnd });
      if (initialConfig.specificCalendarDays) setSpecificCalendarDays(initialConfig.specificCalendarDays.map(d => ({ label: d, value: d })));
      setDailyEveryXDays(initialConfig.everyXDays || false);
      setDailyEveryXDaysValue(initialConfig.everyXDaysValue || '2');
      setShowHourlyConfig(!!initialConfig.hourlyConfig);
      setHourlyConfig(initialConfig.hourlyConfig || {});
    }
  }, [initialConfig]);

  const workingDayOptions = getWorkingDayOptions();
  const calendarDayOptions = getCalendarDayOptions();

  const getWDIndex = (val) => {
    if (val === 'WD-1') return -1;
    return parseInt(val.replace('WD', '')) || 0;
  };

  const workingDayStartOptions = workingDayOptions.filter(o => workingDayOptions.some(e => getWDIndex(e.value) > getWDIndex(o.value)));
  const filteredWorkingDayEndOptions = workingDayOptions.filter(
    opt => getWDIndex(opt.value) > getWDIndex(dailyWorkingDayBegin?.value)
  );

  const calendarDayStartOptions = calendarDayOptions.filter(o => calendarDayOptions.some(e => parseInt(e.value) > parseInt(o.value)));
  const filteredCalendarDayEndOptions = calendarDayOptions.filter(
    opt => parseInt(opt.value) > parseInt(dailyCalendarDayBegin?.value)
  );

  const handleConfigChange = (overrides = {}) => {
    onConfigChange({
      workingDay: dayType === 'working',
      workingDayOption,
      workingDayBegin: dailyWorkingDayBegin?.value,
      workingDayEnd: dailyWorkingDayEnd?.value,
      specificWorkingDays: specificWorkingDays.map(d => d.value),
      calendarDay: dayType === 'calendar',
      calendarDayOption,
      calendarDayBegin: dailyCalendarDayBegin?.value,
      calendarDayEnd: dailyCalendarDayEnd?.value,
      specificCalendarDays: specificCalendarDays.map(d => d.value),
      everyXDays: dailyEveryXDays,
      everyXDaysValue: dailyEveryXDaysValue,
      hourlyConfig: showHourlyConfig ? hourlyConfig : null,
      ...overrides
    });
  };

  if (isViewMode) {
    return (
      <SpaceBetween size="s">
        {dayType === 'working' && workingDayOption === 'range' && (
          <Box>
            <Box variant="awsui-key-label">Working day range</Box>
            <span><Val>{dailyWorkingDayBegin?.label}</Val> to <Val>{dailyWorkingDayEnd?.label}</Val></span>
          </Box>
        )}
        {dayType === 'working' && workingDayOption === 'specific' && (
          <Box>
            <Box variant="awsui-key-label">Specific working days</Box>
            <Val>{specificWorkingDays.map(d => d.label).join(', ') || '—'}</Val>
          </Box>
        )}
        {dayType === 'calendar' && calendarDayOption === 'range' && (
          <Box>
            <Box variant="awsui-key-label">Calendar day range</Box>
            <span>Day <Val>{dailyCalendarDayBegin?.label}</Val> to day <Val>{dailyCalendarDayEnd?.label}</Val></span>
          </Box>
        )}
        {dayType === 'calendar' && calendarDayOption === 'range' && dailyEveryXDays && (
          <Box>
            <Box variant="awsui-key-label">Recurrence</Box>
            <span>Every <Val>{dailyEveryXDaysValue}</Val> days</span>
          </Box>
        )}
        {dayType === 'calendar' && calendarDayOption === 'specific' && (
          <Box>
            <Box variant="awsui-key-label">Specific calendar days</Box>
            <Val>{specificCalendarDays.map(d => d.label).join(', ') || '—'}</Val>
          </Box>
        )}
        {showHourlyConfig && hourlyConfig && Object.keys(hourlyConfig).length > 0 && (
          <Box>
            <Box variant="awsui-key-label">Hourly configuration</Box>
            <HourlyTab isViewMode={true} onConfigChange={() => {}} initialConfig={hourlyConfig} />
          </Box>
        )}
      </SpaceBetween>
    );
  }

  return (
    <SpaceBetween size="m">
      <RadioGroup
        value={dayType}
        onChange={({ detail }) => {
          setDayType(detail.value);
          if (detail.value === 'working') {
            setCalendarDayOption('range');
            setSpecificCalendarDays([]);
            setDailyEveryXDays(false);
            handleConfigChange({ workingDay: true, calendarDay: false, calendarDayOption: 'range', specificCalendarDays: [], everyXDays: false });
          } else {
            setWorkingDayOption('range');
            setSpecificWorkingDays([]);
            handleConfigChange({ workingDay: false, calendarDay: true, workingDayOption: 'range', specificWorkingDays: [] });
          }
        }}
        items={[
          { value: 'working', label: 'Working day' },
          { value: 'calendar', label: 'Calendar day' },
        ]}
        disabled={isViewMode}
      />

      {dayType === 'working' && (
        <Box margin={{ left: 'xxl', top: 'xs' }}>
          <SpaceBetween size="s">
            <Alert type="info">
              All working day schedules added will take effect from next UTC day.
            </Alert>
            <RadioGroup
              value={workingDayOption}
              onChange={({ detail }) => {
                setWorkingDayOption(detail.value);
                if (detail.value === 'range') {
                  setSpecificWorkingDays([]);
                  handleConfigChange({ workingDayOption: 'range', specificWorkingDays: [] });
                } else {
                  handleConfigChange({ workingDayOption: 'specific', workingDayBegin: null, workingDayEnd: null });
                }
              }}
              items={[
                { value: 'range', label: 'Begin On / End On' },
                { value: 'specific', label: 'Choose specific working days' },
              ]}
              disabled={isViewMode}
            />
            {workingDayOption === 'range' && (
              <Box margin={{ left: 'xxl', top: 'xs' }}>
                <Grid gridDefinition={[{ colspan: 2 }, { colspan: 3 }, { colspan: 2 }, { colspan: 3 }]}>
                  <Box padding={{ top: 'xs' }}>Begin On</Box>
                  <Select selectedOption={dailyWorkingDayBegin} onChange={({ detail }) => {
                    setDailyWorkingDayBegin(detail.selectedOption);
                    if (getWDIndex(dailyWorkingDayEnd?.value) <= getWDIndex(detail.selectedOption.value)) {
                      const newEnd = workingDayOptions.find(o => getWDIndex(o.value) > getWDIndex(detail.selectedOption.value));
                      setDailyWorkingDayEnd(newEnd || null);
                      handleConfigChange({ workingDayBegin: detail.selectedOption.value, workingDayEnd: newEnd?.value });
                    } else {
                      handleConfigChange({ workingDayBegin: detail.selectedOption.value });
                    }
                  }} options={workingDayStartOptions} disabled={isViewMode} />
                  <Box padding={{ top: 'xs' }}>End On</Box>
                  <Select selectedOption={dailyWorkingDayEnd} onChange={({ detail }) => { setDailyWorkingDayEnd(detail.selectedOption); handleConfigChange({ workingDayEnd: detail.selectedOption.value }); }} options={filteredWorkingDayEndOptions} disabled={isViewMode} />
                </Grid>
              </Box>
            )}
            {workingDayOption === 'specific' && (
              <Box margin={{ left: 'xxl', top: 'xs' }}>
                <Multiselect
                  selectedOptions={specificWorkingDays}
                  onChange={({ detail }) => { setSpecificWorkingDays(detail.selectedOptions); handleConfigChange({ specificWorkingDays: detail.selectedOptions.map(d => d.value) }); }}
                  options={workingDayOptions}
                  placeholder="Choose working days (e.g., WD1, WD5)"
                  disabled={isViewMode}
                />
              </Box>
            )}
          </SpaceBetween>
        </Box>
      )}

      {dayType === 'calendar' && (
        <Box margin={{ left: 'xxl', top: 'xs' }}>
          <SpaceBetween size="s">
            <RadioGroup
              value={calendarDayOption}
              onChange={({ detail }) => {
                setCalendarDayOption(detail.value);
                if (detail.value === 'range') {
                  setSpecificCalendarDays([]);
                  handleConfigChange({ calendarDayOption: 'range', specificCalendarDays: [] });
                } else {
                  setDailyEveryXDays(false);
                  handleConfigChange({ calendarDayOption: 'specific', calendarDayBegin: null, calendarDayEnd: null, everyXDays: false });
                }
              }}
              items={[
                { value: 'range', label: 'Begin On / End On' },
                { value: 'specific', label: 'Choose specific calendar days' },
              ]}
              disabled={isViewMode}
            />
            {calendarDayOption === 'range' && (
              <Box margin={{ left: 'xxl', top: 'xs' }}>
                <SpaceBetween size="s">
                  <Grid gridDefinition={[{ colspan: 2 }, { colspan: 3 }, { colspan: 2 }, { colspan: 3 }]}>
                    <Box padding={{ top: 'xs' }}>Begin On</Box>
                    <Select selectedOption={dailyCalendarDayBegin} onChange={({ detail }) => {
                      setDailyCalendarDayBegin(detail.selectedOption);
                      if (parseInt(dailyCalendarDayEnd?.value) <= parseInt(detail.selectedOption.value)) {
                        const newEnd = calendarDayOptions.find(o => parseInt(o.value) > parseInt(detail.selectedOption.value));
                        setDailyCalendarDayEnd(newEnd || null);
                        handleConfigChange({ calendarDayBegin: detail.selectedOption.value, calendarDayEnd: newEnd?.value });
                      } else {
                        handleConfigChange({ calendarDayBegin: detail.selectedOption.value });
                      }
                    }} options={calendarDayStartOptions} disabled={isViewMode} />
                    <Box padding={{ top: 'xs' }}>End On</Box>
                    <Select selectedOption={dailyCalendarDayEnd} onChange={({ detail }) => { setDailyCalendarDayEnd(detail.selectedOption); handleConfigChange({ calendarDayEnd: detail.selectedOption.value }); }} options={filteredCalendarDayEndOptions} disabled={isViewMode} />
                  </Grid>
                  <Grid gridDefinition={[{ colspan: 2 }, { colspan: 2 }, { colspan: 8 }]}>
                    <Checkbox checked={dailyEveryXDays} onChange={({ detail }) => { setDailyEveryXDays(detail.checked); handleConfigChange({ everyXDays: detail.checked }); }} disabled={isViewMode}>Every</Checkbox>
                    <Select
                      selectedOption={{ label: dailyEveryXDaysValue, value: dailyEveryXDaysValue }}
                      onChange={({ detail }) => { setDailyEveryXDaysValue(detail.selectedOption.value); handleConfigChange({ everyXDaysValue: detail.selectedOption.value }); }}
                      options={[...Array(30)].map((_, i) => ({ label: String(i + 1), value: String(i + 1) }))}
                    />
                    <Box padding={{ top: 'xs' }}>days</Box>
                  </Grid>
                </SpaceBetween>
              </Box>
            )}
            {calendarDayOption === 'specific' && (
              <Box margin={{ left: 'xxl', top: 'xs' }}>
                <Multiselect
                  selectedOptions={specificCalendarDays}
                  onChange={({ detail }) => { setSpecificCalendarDays(detail.selectedOptions); handleConfigChange({ specificCalendarDays: detail.selectedOptions.map(d => d.value) }); }}
                  options={calendarDayOptions}
                  placeholder="Choose calendar days (e.g., 1, 15, 31)"
                  disabled={isViewMode}
                />
              </Box>
            )}
          </SpaceBetween>
        </Box>
      )}

      <Box>
        <Button variant="normal" onClick={() => { setShowHourlyConfig(!showHourlyConfig); handleConfigChange({ hourlyConfig: !showHourlyConfig ? hourlyConfig : null }); }}>
          {showHourlyConfig ? 'Hide' : 'Add'} Hourly Configuration
        </Button>
      </Box>

      {showHourlyConfig && (
        <Box padding={{ left: 'l' }}>
          <Box variant="h3">Hourly Configuration</Box>
          <HourlyTab isViewMode={false} onConfigChange={(config) => { setHourlyConfig(config); handleConfigChange({ hourlyConfig: config }); }} initialConfig={initialConfig.hourlyConfig || { option: 'interval', interval: '1', selectedHours: [], rangeStart: '9', rangeEnd: '17', specificMinute: '0' }} />
        </Box>
      )}
    </SpaceBetween>
  );
};

export default DailyTab;
